import { VERSION_SLUG_PATTERN } from "./instance-identity.js";
import { APP_POOL_EGRESS_MODES, APP_POOL_NODE_GROUP_ID_PATTERN, SATELLITE_PROVIDERS, appPoolAutoscalerNameSchema, appPoolAutoscalerSchema, appPoolBindingSchema, appPoolNameSchema, appPoolRegionSchema, appPoolSchema, machineTypeCatalogEntrySchema } from "@zion/sdk-contracts";
import { z } from "zod";
import { createHash } from "node:crypto";
//#region src/desired-state.ts
/**
* The most entries the desired-state member `appPoolTierCaps` carries: 1024. A data plane holds at
* most `APP_POOL_NODE_GROUP_MAX` (100) app-pool node groups, so 1024 leaves room for overrides that
* wait for their tiers. One entry is at most 232 bytes of compact JSON: a 36-character org id, a
* 63-character app key, a 40-character pool name, a 34-character tier key and a 3-digit cap. So a
* full member is at most 238,593 bytes, about 233 KiB.
*/
const APP_POOL_TIER_CAPS_MAX = 1024;
/**
* 2026-10-14-aws-dp-parity D2. How the app's author approved an app for an org:
*
* - `public-pin`: the app has a pin on a public channel, which approves it for every org.
* - `channel-share`: an unrevoked private channel share names the org, which approves the app for
*   that org alone.
*
* Runtime schema: `AppOfferApprovalSchema`. Pure data.
*/
const APP_OFFER_APPROVALS = ["public-pin", "channel-share"];
/**
* 2026-10-26-aws-followups D-8. The clouds that a per-app identity can live in: `gcp` (a Google
* service account per app) and `aws` (an IAM role per app). A local data plane composes no App XR,
* so it has no member here.
*
* Runtime schema: the `provider` member of `PerAppIdentitySchema`. Pure data.
*/
const PER_APP_IDENTITY_PROVIDERS = ["gcp", "aws"];
/**
* WS-7 (spine D6): the cloud-neutral confinement level that the platform granted to a fixture.
*
* R2 of `2026-10-14-app-pools-runtime-deletion` (R-081 item 2) removed `AppFixtureSet.isolation`,
* the one wire member that carried a level. No CP sends a level and no operator reads one, so
* this vocabulary grants nothing now. The text below records what the level meant before R2.
*
* The level was never app-declarable. It was a CP-authored field on the typed sidecar, never a key
* in `app.json` and never inside the opaque `appManifest` blob. Spine D1: a sandbox an app can
* configure is not a sandbox (#1710).
*
* The level was deliberately not a `runtimeClassName` (D6). `gvisor` is a GKE handler name; Azure
* AKS maps to `kata-mshv-vm-isolation` and AWS EKS to a Fargate profile or Kata. A substrate's
* handler name here forces the next substrate to redesign instead of map. Before R2 the operator
* read the substrate-to-handler mapping from one DP chart value, `SANDBOXED_RUNTIME_CLASS`. The
* R2 operator reads neither the level nor that value. R2 kept the value in the DP chart for an
* operator pod of release N-1 during the roll (R-086 item 4), and R3 removed it (R-092 item 4).
*
* - `standard`: the platform default. No runtime confinement beyond the shared kernel.
* - `sandboxed`: the fixture's pods ran under the substrate's sandboxed runtime.
*
* WS-7 §7 records the cost and the compatibility tail of `sandboxed`: it was a rare escalation,
* not a hardening default.
*
* @deprecated `2026-09-04-app-pools`. Superseded by `APP_POOL_WORKLOADS` and `AppPoolWorkload`
* in `@zion/sdk-contracts`' `app-pool.ts`, as the confinement discriminant for pool work. This is
* neither a rename nor a one-to-one map. The pair here names a kernel confinement level, gVisor or
* not, that the platform grants to a fixture. The successor names whether the pod sits in the
* service mesh under the fixture's identity, or outside it under none. The kernel posture rides
* {@link AppPoolGrant} instead. The envelope carries no isolation level since R2 of
* `2026-10-14-app-pools-runtime-deletion` (R-081 item 2). The constant and its type stay because
* R-081 item 2 keeps the type in the barrel, with its zod twin (D-Q2-2 of the plan's `Q2.md`). No
* code outside this package imports either. The tag means "build no new pool surface on it".
*/
const FIXTURE_ISOLATION_LEVELS = ["standard", "sandboxed"];
/**
* The substrates a data plane can run on, as the CP names them.
*
* Read by WS-4's compiler through {@link DesiredStateEnvelope.provider}. Every member carries
* weight:
* - `'gcp'`: a GKE data plane. The `dataplane` Composition's node-pool fragments render for it.
* - `'local'`: the k3d dev substrate, where the node-pool fragments render nothing (chain D hop 6).
* - `'aws'`: an EKS data plane, composed by the `dataplane-aws` Composition, which renders EKS
*   node groups (plan 2026-08-25-dp-aws-port, R-056 item 1). In release 1 the operator compiler
*   must refuse app pools on an `'aws'` data plane with a status that names the reason, and must
*   compile no GCP node group there. The operator workstream WS-9c owns that arm, in the same stage
*   as this member. See {@link DesiredStateEnvelope.provider} for what a compiler with no `'aws'`
*   arm does.
*
* This is not a cloud-provider enum to grow. A member joins only in the change that makes a
* Composition render its data plane. A member the renderer does not know would compile a group
* nothing provisions. `'aws'` is last, so the position of the two older members does not move.
*/
const DP_PROVIDERS = [
	"gcp",
	"local",
	"aws"
];
/**
* Runtime validator for {@link DesiredStateEnvelope.provider}.
*
* It exists because the envelope schema does not check this member. `DesiredStateEnvelopeSchema`
* checks eleven paths: nine are containers, and two (`appManifest`, `fixtureManifest`) are presence
* checks that accept any value except `undefined` and `null`. This member is not one of them, so a
* consumer that wants to police this field parses it with this schema at its own boundary. WS-4
* does that. The schema refuses an unknown value and does not read it as `'gcp'`. Absence is the
* back-compat arm, described on the member's docblock. A wrong value is a CP bug. A consumer that
* read the two as one would render app-pool pods on a substrate that nobody meant.
*
* `'gcp'`, `'local'` and `'aws'` parse to themselves. Anything else, `'azure'`, `''`, `'GCP'` and
* `undefined` included, is a `ZodError`.
* Wrap it in `.optional()` at a site where absence is legal. Pure.
*/
const dpProviderSchema = z.enum(DP_PROVIDERS);
/**
* One enabled satellite, as the CP projects it to the operator in
* {@link DesiredStateEnvelope.satellites} (2026-09-29-gpu-and-regional-app-pools D-5, D-9, D-14).
*
* Authored by release-engine from one `provisioning.dp_satellites` row (WS-8). Read by the operator:
* WS-9's placement resolves a regional pool's machine type against `enabledMachineTypes`, and WS-10
* accepts satellite status only for a listed `region`.
*
* - `provider`: one of {@link SATELLITE_PROVIDERS}. The admin PUT refuses any other value with a
*   400, so a satellite on another cloud never reaches the envelope.
* - `region`: the satellite's region in `APP_POOL_REGION_PATTERN`'s grammar (DF-1), the value a
*   pool's `region` names and the token of every satellite NATS subject.
* - `enabledMachineTypes`: the machine types the satellite's node groups may use, full catalog
*   entries canonicalised by the PUT against `authoritativeCatalog(region, 'gcp')`. At least one:
*   a satellite with no machine type can place nothing, and the PUT refuses an empty selection.
*
* `.strict()`. A conforming object parses to itself; an unknown provider, a malformed region, an
* empty machine-type list or an unknown key is a `ZodError`. The envelope is cast and never
* parsed as a whole (A-170), so a consumer parses each entry with this schema at its own boundary
* and drops an entry that fails, which leaves that region's pools unschedulable. Pure.
*/
const SatelliteSpecSchema = z.object({
	provider: z.enum(SATELLITE_PROVIDERS),
	region: appPoolRegionSchema,
	enabledMachineTypes: z.array(machineTypeCatalogEntrySchema).min(1)
}).strict();
/**
* The one grammar for a digest-pinned, registry-portable image reference on this wire:
* `<repo>@sha256:<64 lowercase hex>`, optionally `<namespace>/<repo>@…`.
*
* Used by {@link AppPoolDesiredStateSchema} and {@link AppPoolAutoscalerDesiredStateSchema}.
*
* There is no registry prefix, by intent. A registry with a port such as `zion-registry:5000`
* carries a colon, and no character class here admits one, so the full `<reg>/<repo>@sha256:<hex>`
* form is refused at this boundary. Each consumer builds the pull URL from its own
* `IMAGE_REGISTRY`. That is the rule `desired-state.schemas.ts`' `IMAGE_REF` records for the
* fixture wire, and the reason `oci-bundle-fetcher` strips the registry before the annotation is
* stored.
*
* A tag is refused, and that refusal carries the argument. An operator handed a mutable reference
* would pull whatever the tag pointed at when the node happened to schedule. Two attempts of one
* run could then execute two different images. `sha512` is refused too, so there is one digest
* algorithm on the wire. A second algorithm is a second thing every comparison has to normalise.
*
* This pattern is byte-for-byte `release-engine`'s `DIGEST_PIN_RE` in `bundle-parser.ts`, and that
* equality is the argument for its shape. It is the grammar the publish door already enforces on
* the `zion.fixture.image` OCI annotation, so a reference that published always projects. An
* earlier draft used the stricter OCI repository grammar, lower case only with separators between
* two alphanumerics. It was measured to refuse four forms the publish door accepts:
* `Foo@sha256:...`, `a__b@sha256:...`, `-notes@sha256:...` and `notes-@sha256:...`. Each of those
* would have been a validly published app whose pools the CP could not emit desired state for. The
* refusal would land at projection, where nothing names the manifest as the cause. A wire grammar
* narrower than the door feeding it is a fail-closed defect and not extra safety.
*
* One grammar serves this package. `desired-state.schemas.ts`' module-private `IMAGE_REF`, the
* fixture wire's own pinned-reference field, imports this constant instead of restating the
* literal. Two copies of one regex in one package is the X-18 shape.
*
* The byte-equal test lives in WS-3 and not here, because `@zion/sdk-cp-dp-contracts` cannot
* import release-engine, so no test in this package can compare the two literals. This package's
* own spec asserts that the four divergence cases are accepted, the observable half of the claim.
*/
const DIGEST_PINNED_IMAGE_REF_PATTERN = /^[A-Za-z0-9._-]+(\/[A-Za-z0-9._-]+)*@sha256:[a-f0-9]{64}$/;
/**
* Runtime validator for {@link DIGEST_PINNED_IMAGE_REF_PATTERN}. A pinned reference parses to
* itself. A tag, a bare repository, a `sha512` digest, a registry-prefixed form or an over-length
* value is a `ZodError`. Pure.
*/
const digestPinnedImageRefSchema = z.string().regex(DIGEST_PINNED_IMAGE_REF_PATTERN).max(512);
/** Cap on `imageFixtureVersion`. The value is an app-authored version string, so it is bounded
*  here instead of left open on a wire the publish door feeds. */
const APP_POOL_IMAGE_FIXTURE_VERSION_MAX_LENGTH = 128;
/**
* The CP-authored security posture for one out-of-mesh pool. This is chain B.
*
* Authored by `deriveAppPoolGrants` in release-engine, which answers the owner version's own
* declaration for the pool name: the owner is the active version with the highest version number
* (2026-09-21-pool-image-per-version-and-door-token-rotation, R-001, L-3). `egress`,
* `containerRuntime` and `writableRootFS` are as the owner declared them, absent booleans read as
* `false`. A name the owner declares out-of-mesh and another active version declares in-mesh
* takes the closed floor. Read by `buildAppPoolPod`, which reads this struct and never the
* manifest field.
*
* The app requests a posture and never authors one. `AppPoolOutOfMesh` carries `egress`,
* `containerRuntime` and `writableRootFS` as a request. This struct is the platform's answer, and
* the two are different claims. The grant can never exceed the owner's declaration, and
* {@link assertAppPoolGrantConsistent} is the backstop that refuses one wider than the owner's
* `pool`. Until 2026-09-21 this was the meet across every active version; R-001 of the plan named
* above made the spec, posture included, the owner's.
*
* Every member is required. An optional security field re-creates the absent-because-unpopulated
* versus absent-because-the-producer-predates-it ambiguity that has already shipped on this wire
* once, as the removed `WorkTargetGrant.hostAccess` recorded. Additivity lives on the grant as a
* whole, because {@link AppPoolDesiredState.grant} is nullable.
*
* `.strict()` makes an unknown key a parse error, so a relaxation the CP does not name cannot ride.
*/
const AppPoolGrantSchema = z.object({
	/** The network posture. `'closed'` adds no egress rule at all. `'open'` adds `0.0.0.0/0` minus
	*  the DP's denied CIDRs (chain E hop 5). The pair `containerRuntime: true` with
	*  `egress: 'closed'` is refused at publish, so it never reaches a grant. */
	egress: z.enum(APP_POOL_EGRESS_MODES),
	/** Whether the pod gets the host-facing relaxations a container runtime needs: `procMount:
	*  Unmasked`, both LSM profiles `Unconfined`, and `capabilities.add` taken from the constant
	*  `ISOLATED_POOL_HOST_ACCESS_CAPABILITIES` and nothing else (chain E hop 3). The capability
	*  list is never app-selectable. */
	containerRuntime: z.boolean(),
	/** Whether the pod may write its own root filesystem, rendering as
	*  `readOnlyRootFilesystem: !writableRootFS`. Kubernetes inverts the polarity of the grant, so
	*  the negation sits at the render site and this member reads the way the grant reads.
	*  CLAUDE.md classes the root filesystem on the container-runtime arm as ergonomics and not
	*  containment. That pod already holds `CAP_SYS_ADMIN` in its own namespaces with both LSM
	*  profiles `Unconfined`, so it can mount over those paths anyway. The boundary is the network
	*  and the identity. */
	writableRootFS: z.boolean()
}).strict();
/**
* One pool, as the CP projects it to the operator. It carries the manifest entry verbatim, plus
* the two things only the CP can know: the digest the image resolves to, and the grant.
*
* Authored by WS-3's `mapAppPoolsToDesiredState`, which re-parses it with this schema before
* emitting. Read by WS-4's `app-pool-catalog.ts`. Rides `AppDesiredState.pools[]`.
*
* A conforming object parses to itself. A pool whose `grant` does not match its `workload` is a
* `ZodError` on `grant`, from the `.refine` below (chains B and G, hop 4). An unknown key anywhere
* is a parse error, from `.strict()` here and inside `appPoolSchema`'s two arms. Pure.
*
* `pool` holds the manifest entry itself and not a projection of it, re-parsed on the CP with
* `appPoolSchema`, the same schema the publish door used. Field-by-field projection of a manifest
* subtree onto the wire is the work the 2026-05-14 envelope refactor removed, so a pool is carried
* whole. It is still not opaque. Unlike `fixtureManifest`, this subtree is typed on the wire,
* because the operator makes placement and containment decisions from it. An opaque blob would put
* a `z.record(z.unknown())` in front of a security-relevant read.
*
* ## ⛔ ONE ENTRY PER POOL NAME PER APP, FROM THE OWNER VERSION (R-055)
*
* `AppDesiredState.pools[]` carries at most one entry for any `name`, and there is NO version
* dimension on this shape to key on. That is a contract on the producer, not a schema `.refine`:
* the array is `AppDesiredState.pools` and this schema sees one element.
*
* Two active versions of one app can both declare `render`, and the consumer has one slot for the
* answer. WS-4's catalog keys the held snapshot `` `${appKey}/${name}` `` and its reconciler calls
* `resolveAll(appKey, app.appVersion, app.pools, …)` once per app with a single `appVersion`, so a
* second entry with the same `name` does not become a second pool — it silently replaces the first,
* and which one survives is decided by the order the CP happened to read the contributing
* compositions in. `imageRef`, {@link AppPoolDesiredState.imageFixtureVersion} and every knob on
* `pool` would then be resolved by Map insertion order rather than by any stated rule.
*
* So WS-3's `mapAppPoolsToDesiredState` emits the OWNER version's entry for each name: the caller
* sorts the app's active versions highest-semver-first with the same `compareSemver` comparator
* `AppFixtureSet.ownerVersion` uses, and the first one declaring the name owns it. It is the same
* comparator and not the same binding: this pick is per app and per pool name, tiebreaks on the
* version string, and speaks `appVersion`, where `ownerVersion` is per fixture, tiebreaks on
* `compositionId` and speaks `versionSlug`. A second active version declaring the same name emits
* nothing, and when its `workload` differs the projection's caller logs the conflict.
*
* The GRANT is the owner's declaration too (R-001, L-3 of
* 2026-09-21-pool-image-per-version-and-door-token-rotation): the entry and the posture come from
* one version. A per-attempt pod on this pool may run an older active version's image, chosen by
* the pod that submitted the run, under the owner's posture; two versions of one app are one
* publisher's code under one app identity, so the widening stays inside the app's own authority
* (`security-audit.md` chain B hop 3 of that plan). The owner's arm decides the grant SHAPE:
* `null` for an in-mesh owner, the owner's declaration for an out-of-mesh owner, and the closed
* floor when the app's versions disagree about the workload. Handing an in-mesh entry the
* out-of-mesh floor is the pairing the `.refine` below refuses and
* {@link assertAppPoolGrantConsistent} throws on, so it would make every pod render for that pool
* throw.
*/
const AppPoolDesiredStateSchema = z.object({
	/** The pool's name, the key it had in `app.json` `pools{}`. Becomes the pod label
	*  `zion.witness.ai/app-pool`, hence the label-safe, 40-character grammar. */
	name: appPoolNameSchema,
	/** The manifest entry verbatim, re-parsed with the publish door's own schema. */
	pool: appPoolSchema,
	/**
	* The digest-pinned reference for the fixture the pool's `image.fixture` names, read from
	* `app_versions.image_ref` for that fixture (`research/cp-publish-and-desired-state.md` §1).
	*
	* CP-authored. The manifest names a fixture and not an image, so the app cannot choose what
	* runs. The CP resolves the name to a digest the publish door already pinned.
	*/
	imageRef: digestPinnedImageRefSchema,
	/**
	* The app version `imageRef` was read from. It is the provenance of the digest and not a
	* second selector, and the operator must not resolve the image from it. It is here so a
	* running pool can be traced to the release that pinned it, which a bare digest cannot answer.
	*/
	imageFixtureVersion: z.string().min(1).max(APP_POOL_IMAGE_FIXTURE_VERSION_MAX_LENGTH),
	/**
	* The CP-authored posture, or `null` for an in-mesh pool.
	*
	* The value is `null` and not absent for in-mesh, pinned in both directions by the `.refine`
	* below. An in-mesh pool runs the app's own code under the fixture's own identity and cannot
	* spell a posture at all (chain G hop 4). So `null` states positively that there is nothing to
	* apply. Optionality would make "in-mesh, nothing to grant" and "out-of-mesh, producer forgot"
	* indistinguishable at the one read `buildAppPoolPod` makes.
	*/
	grant: AppPoolGrantSchema.nullable()
}).strict().refine((p) => p.pool.workload === "out-of-mesh" === (p.grant !== null), {
	message: "grant is required for an out-of-mesh pool and must be null for an in-mesh pool",
	path: ["grant"]
});
/** Digest characters carried by a `bindingId`. 20, the same slice
*  {@link appPoolNodeGroupId} and the isolated/dedicated pool ids take. */
const BINDING_ID_HEX_LENGTH = 20;
/** The separator between the three hashed binding fields. The NUL byte, for the injectivity
*  reason {@link appPoolNodeGroupId} records. Built from its code point instead of typed as an
*  escape, so this file stays greppable. */
const BINDING_ID_FIELD_SEPARATOR = String.fromCharCode(0);
/**
* The stable identity of one binding. The id is
* `sha256(pool NUL JSON(on) NUL JSON(payload))` in hex, sliced to 20 characters.
*
* This function goes beyond the 2026-09-04-app-pools plan's `design-docs/WS-2.md` §3 signature list, which names the formula but
* no function. `AppPoolBindingDesiredState.bindingId` has to be computed somewhere. WS-3 emits it
* and WS-5 keys `work.app_pool_schedules` rows on it, and this platform keeps one derivation site
* per derived name. Two callers each spelling the formula is the defect this repo keeps finding.
*
* Called by WS-3 when it projects `app.json` `bindings[]`. Pure, and the hash is `node:crypto`'s
* `createHash('sha256')`.
*
* Input is an `AppPoolBinding`: `pool`, the `on` trigger (a schedule or an event), and the
* optional static `payload`. Output is 20 lowercase hex characters, for every input. There is no
* error class. Any value this type admits hashes, and the grammar of the parts belongs to
* `appPoolBindingSchema`.
*
* Invariants:
*  - Stable across releases for an unchanged binding, which is the reason this id exists. WS-5's
*    schedule row carries a `fired_through` watermark, and a republish that leaves the binding
*    alone must not reset it. A reset re-fires or skips occurrences with nothing red anywhere.
*  - Changing `pool`, the trigger or the payload changes the id. A rewritten binding is therefore a
*    new row, and the old row's watermark is discarded instead of inherited.
*  - An absent `payload` hashes as `null` and not as the string `undefined`.
*    `JSON.stringify(undefined)` returns `undefined` and not a string, which would concatenate as
*    `'undefined'` and collide with a binding whose payload was that string.
*
* Key order inside `on` and `payload` participates, because `JSON.stringify` emits insertion
* order. That is safe for the stated purpose, and not by luck. The binding reaches WS-3 by parsing
* the stored `app.json` bytes, so the order is the author's and is identical on every republish of
* one version. An author who reorders keys is editing the manifest, and a new id is the correct
* answer there. Do not repair this with a canonicalising stringify. That would change every shipped
* id, and this is a wire identity two planes compare.
*/
function appPoolBindingId(binding) {
	const material = [
		binding.pool,
		JSON.stringify(binding.on),
		JSON.stringify(binding.payload ?? null)
	].join(BINDING_ID_FIELD_SEPARATOR);
	return createHash("sha256").update(material).digest("hex").slice(0, BINDING_ID_HEX_LENGTH);
}
/** The `bindingId` grammar: 20 lowercase hex characters, {@link appPoolBindingId}'s output. It
*  carries no prefix, unlike `groupId`'s `apg-`. A `bindingId` never becomes a Kubernetes object
*  name, so it needs no namespace to keep it apart from another id family. */
const APP_POOL_BINDING_ID_PATTERN = /^[0-9a-f]{20}$/;
/**
* One binding, as the CP projects it: the manifest entry verbatim plus its derived id.
*
* Authored by WS-3. Read by WS-5's `app-pool-schedule-reconciler.ts`, which owns one
* `work.app_pool_schedules` row per `(binding, resident org)`, and by `app-pool-event-bridge.ts`,
* which names its NATS durable after the binding. Rides `AppDesiredState.bindings[]`.
*
* A conforming object parses to itself. A `bindingId` outside
* {@link APP_POOL_BINDING_ID_PATTERN}, a malformed `binding`, or any unknown key is a `ZodError`.
* `.strict()`. Pure.
*
* The id is carried and not re-derived by the reader, and the schema does not check that it equals
* `appPoolBindingId(binding)`. That check belongs to WS-3, where both sides are in hand. A
* `.refine` here would make every operator recompute a CP-authored value on every poll, and would
* turn a CP bug into a whole-envelope parse failure on the DP.
*/
const AppPoolBindingDesiredStateSchema = z.object({
	/** The manifest entry verbatim, re-parsed with the publish door's own schema. */
	binding: appPoolBindingSchema,
	/** {@link appPoolBindingId}'s output for `binding`. CP-computed and not DP-supplied. */
	bindingId: z.string().regex(APP_POOL_BINDING_ID_PATTERN)
}).strict();
/**
* Every reason the CP declines to provision a compiled node group (R-010).
*
* One member today. `unpriced-node-type` means the group's resolved `nodeType` has no
* `metering.pricing_rates` row keyed `node_hours:<nodeType>` for that data plane's
* `cloud_provider`. `MeteringAggregatorService` prices dedicated and GPU pools with that same key,
* so a machine type missing from it bills every node-hour at zero.
* `AppPoolNodeGroupReconcileService` refuses the PATCH rather than provision capacity nobody is
* charged for. That refusal is the `sizeClass: "xlarge"` defect this feature exists to end (SEC-9).
*
* A closed enum and not free text. WS-4 copies the value into
* `AppPoolReportedStatus.unschedulableReason`, which the console renders to an operator and the
* `pools/describe` route returns to the app. A free string would put CP-side wording on both
* surfaces with no vocabulary anyone can translate or count.
*
* Add a member here and WS-4 gains an arm; the enum is the list both planes agree on.
*/
const REFUSED_NODE_GROUP_REASONS = ["unpriced-node-type"];
/**
* One node group the CP compiled, refused, and told the data plane about.
*
* Authored by WS-3's `AppPoolNodeGroupReconcileService`, one entry per group it declines to PATCH
* onto the DataPlane XR. Read by WS-4, which matches each pool's compiled `groupId` against the
* array and reports that pool as `placement: 'unschedulable'` with `unschedulableReason` set to
* the `reason` here. Rides {@link AppDesiredState.refusedNodeGroups}. Without this member the pool
* sits at zero nodes and the author has nothing to read.
*
* `groupId` is {@link appPoolNodeGroupId}'s output, guarded by `APP_POOL_NODE_GROUP_ID_PATTERN`,
* the one declaration of the `apg-` grammar (SEC-11). It carries no `.max()` because the pattern
* is anchored and fixes the length at 24 characters. `reason` is one of
* {@link REFUSED_NODE_GROUP_REASONS}.
*
* A conforming object parses to itself. An id outside the `apg-` namespace, an unlisted reason, a
* missing member or any unknown key is a `ZodError`. `.strict()`. Pure, with no side effects.
*/
const RefusedNodeGroupSchema = z.object({
	groupId: z.string().regex(APP_POOL_NODE_GROUP_ID_PATTERN),
	reason: z.enum(REFUSED_NODE_GROUP_REASONS)
}).strict();
/**
* One autoscaler, as the CP projects it: the manifest entry verbatim plus the resolved digest.
*
* Authored by WS-3. Read by WS-4's harness, which runs the image for whichever algorithm a pool's
* `concurrency`, `warmFloor` or `maxNodes` knob names, and hands it the signals the entry
* declares. Rides `AppDesiredState.autoscalers[]`.
*
* A conforming object parses to itself. A non-pinned `imageRef`, an unknown signal, an empty or
* duplicated signal list, or any unknown key is a `ZodError`. `.strict()`. Pure.
*/
const AppPoolAutoscalerDesiredStateSchema = z.object({
	/** The algorithm name, the key it had in `app.json` `autoscalers{}` and the value a pool's
	*  function knob names. */
	name: appPoolAutoscalerNameSchema,
	/** The manifest entry verbatim, re-parsed with the publish door's own schema. */
	autoscaler: appPoolAutoscalerSchema,
	/**
	* The digest-pinned reference for the fixture the autoscaler's `image.fixture` names.
	*
	* CP-authored, as {@link AppPoolDesiredState.imageRef} is. There is no `imageFixtureVersion`
	* twin here, by intent. An autoscaler is platform-side control logic with no per-attempt trace
	* to reconstruct, so the provenance field a pool needs would be an unread field on this shape.
	*/
	imageRef: digestPinnedImageRefSchema
}).strict();
/**
* The CP-side consistency assertion for one pool and its grant, chain B hop 3. It throws on the
* first violation. The CP calls it before emitting desired state. The operator does not, because
* the operator applies what it is sent.
*
* This is the may-not-be-weaker arm that the removed `assertWorkTargetGrantsConsistent` held, in the
* one direction that matters here. A grant may be narrower than the pool asked for: the conflict
* floor, when another active version declares the name in-mesh. An app that asked for `open` and
* got `closed` is a correctly refused request. A grant may never be wider. "Weaker" in the
* 2026-09-04-app-pools plan's `design-docs/WS-2.md` §3 names the containment that is weakened,
* and not the grant.
*
* This is also not redundant with the publish door, for the R-18 reason of the removed
* `assertWorkTargetGrantsConsistent`. The publish rules police one manifest version, and this
* polices the derived grant.
* A derivation that reads the owner's own declaration cannot produce a grant wider than it; one
* that folds another version's declaration in can, out of versions that each passed publish, and
* no publish door can see that, because every input was valid. This assertion is what catches a
* derivation got wrong.
*
* Inputs. `pool` is the manifest entry, either arm of the union. `grant` is the CP's answer, or
* `null`.
*
* Output per input class:
*  - An in-mesh pool with `grant: null` returns. Nothing was granted and nothing could be.
*  - An out-of-mesh pool whose grant is no wider than the declaration returns.
*  - `grant: null` on an out-of-mesh pool returns. Absence grants nothing, so it cannot be wider.
*    {@link AppPoolDesiredStateSchema}'s `.refine` refuses that pairing. Duplicating the check
*    here would make one violation throw two different error classes from two layers.
*
* Errors, all of class `Error`, thrown in the order the body checks them so a reader can walk the
* list:
*  1. A non-null grant on an in-mesh pool. That pool declares no posture at all, so every field of
*     the grant is wider than nothing.
*  2. `egress: 'open'` where the pool declared `'closed'`.
*  3. `containerRuntime: true` where the pool did not ask.
*  4. `writableRootFS: true` where the pool did not ask.
*
* Arms 1 and 4 go beyond the two cases the 2026-09-04-app-pools plan's `design-docs/WS-2.md` §3
* names, by intent. Arm 4 makes the same claim as arm 3 about the sibling field that plan's
* `security-audit.md` chain B hop 2 names alongside it: `containerRuntime` and `writableRootFS`
* are both relaxations the owner declares. Leaving arm 4 out would let a derivation manufacture
* one of the two undetected. Arm 1 exists because the in-mesh arm of
* the union has no `egress` to compare against, so the narrowing check has to resolve somewhere.
* Refusing is the only reading that does not let a widened in-mesh grant pass.
*
* It throws instead of returning issues because its caller is CP-internal desired-state assembly
* and not a publish door. There is no `FixtureRuleIssue` channel there, and a `severity` would
* have nowhere to go. Pure, with no I/O.
*/
function assertAppPoolGrantConsistent(pool, grant) {
	if (grant === null) return;
	if (pool.workload === "in-mesh") throw new Error(`AppPoolGrant: an in-mesh pool declares no posture, so it cannot be granted one (egress='${grant.egress}', containerRuntime=${grant.containerRuntime}, writableRootFS=${grant.writableRootFS}). An in-mesh pool runs the app's own code under the fixture's own identity; grant must be null.`);
	if (grant.egress === "open" && pool.egress === "closed") throw new Error("AppPoolGrant: egress 'open' granted where the pool declared 'closed'. A grant may narrow a request and never widen it.");
	if (grant.containerRuntime && pool.containerRuntime !== true) throw new Error("AppPoolGrant: containerRuntime granted where the pool did not ask for it. A relaxation is grantable only to a pool that requested it, so the grant is refused here rather than applied to a pod whose author never asked.");
	if (grant.writableRootFS && pool.writableRootFS !== true) throw new Error("AppPoolGrant: writableRootFS granted where the pool did not ask for it. Same rule as containerRuntime: a relaxation follows a request.");
}
/**
* Compute the RFC-1035-safe slug for a fixture version (plan 25 §A.1).
*
* Replaces every `.` with `-` and prepends `v` so the result matches the
* k8s name regex `^[a-z0-9]([a-z0-9-]*[a-z0-9])?$`. When the slug exceeds
* 16 characters (e.g. `1.0.0-rc.1+build.123`), truncates to the first 11
* chars and appends `-{4-hex-of-sha256(version)}` so collisions are
* vanishingly unlikely while the slug stays inside the budget that lets
* `versionedInstanceKey` plus `-worker-{name}` fit under k8s' 63-char
* resource-name cap.
*
* This algorithm is deterministic — given the same version string it
* always returns the same slug — and pure (no I/O), so callers can hash
* locally without round-tripping through the network.
*/
/**
* Collapse hyphen runs and drop leading hyphens, so a body can be spliced into a slug that
* satisfies `VERSION_SLUG_PATTERN`.
*
* ⛔ TRAILING hyphens are deliberately NOT handled here. The caller strips them AFTER slicing,
* because slicing is what can expose one.
*/
function canonicalSlugBody(body) {
	return body.replace(/-{2,}/g, "-").replace(/^-+/, "");
}
function versionSlug(version) {
	if (typeof version !== "string" || version.length === 0) throw new Error("versionSlug: version must be a non-empty string");
	const slug = `v${version.split("+", 1)[0].replace(/[^A-Za-z0-9.-]/g, "-").replace(/\./g, "-").toLowerCase()}`;
	if (slug.length <= 16 && VERSION_SLUG_PATTERN.test(slug)) return slug;
	const suffix = createHash("sha256").update(version).digest("hex").slice(0, 4);
	const stem = canonicalSlugBody(slug.slice(1)).slice(0, 10).replace(/-+$/, "");
	return stem.length > 0 ? `v${stem}-${suffix}` : `v${suffix}`;
}
//#endregion
export { APP_OFFER_APPROVALS, APP_POOL_BINDING_ID_PATTERN, APP_POOL_TIER_CAPS_MAX, AppPoolAutoscalerDesiredStateSchema, AppPoolBindingDesiredStateSchema, AppPoolDesiredStateSchema, AppPoolGrantSchema, DIGEST_PINNED_IMAGE_REF_PATTERN, DP_PROVIDERS, FIXTURE_ISOLATION_LEVELS, PER_APP_IDENTITY_PROVIDERS, REFUSED_NODE_GROUP_REASONS, RefusedNodeGroupSchema, SATELLITE_PROVIDERS, SatelliteSpecSchema, appPoolBindingId, assertAppPoolGrantConsistent, digestPinnedImageRefSchema, dpProviderSchema, versionSlug };

//# sourceMappingURL=desired-state.js.map